import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  Slider,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Gradient as GradientIcon
} from '@mui/icons-material';
import {
  DEFAULT_ANALYSIS_GRID_SETTINGS,
  GridSettings,
  ColorSchemeMode,
  GridScalingMode
} from '../../utils/gridSettings';

// OpenLayers imports
import Map from 'ol/Map';
import Heatmap from 'ol/layer/Heatmap';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';

// Types and interfaces
export interface AnalysisPointDetails {
  probability?: number;
  total_uncertainty?: number;
  uncertainty?: number;
  confidence?: number;
  confidence_interval?: [number, number] | { lower: number; upper: number } | { min: number; max: number } | string | null;
  risk_level?: string;
  model?: string;
  commodity?: string;
  exploration_value?: number;
  drilling_cost?: number;
  expected_roi?: number;
  information_gain?: number;
  [key: string]: unknown;
}

interface HeatMapData {
  probabilities: number[];
  coordinates: [number, number][]; // [lat, lon] pairs
  uncertainties?: number[]; // uncertainty values (0-1) for each point
  pointDetails?: AnalysisPointDetails[]; // original grid point payloads
  metadata?: {
    bounds: [number, number, number, number]; // [min_lat, min_lon, max_lat, max_lon]
    high_potential_count: number;
    discovery_probability: number;
    exploration_value?: number;
  };
}

interface ContinuousHeatMapLayerProps {
  map: Map | null;
  heatMapData: HeatMapData | null;
  mineralType: string;
  visible: boolean;
  onVisibilityChange: (visible: boolean) => void;
  onPointClick?: (point: { lat: number; lon: number; probability: number; uncertainty?: number; details?: AnalysisPointDetails | null }) => void;
  showUncertainty?: boolean;
  threshold?: number;
  blur?: number;
  radius?: number;
  colorSchemeMode?: ColorSchemeMode;
  scalingMode?: GridScalingMode;
  focusRangeOverride?: [number, number] | null;
  customFocusRange?: boolean;
  intensityGamma?: number;
  customGamma?: boolean;
  controlsVariant?: 'panel' | 'legend';
  controlsContainerId?: string;
  onSettingsChange?: (settings: Partial<GridSettings>) => void;
  analysisId?: string; // Add for caching processed features
}

// Enhanced color schemes with multiple modes
const COLOR_SCHEMES = {
  gold: {
    name: 'Gold Potential',
    standard: [
      'rgba(252, 254, 255, 0)',
      'rgba(26, 118, 210, 0.32)',   // deep blue
      'rgba(93, 154, 196, 0.52)',   // steel blue
      'rgba(201, 212, 176, 0.68)',  // pale olive
      'rgba(233, 221, 146, 0.86)',  // light gold
      'rgba(219, 186, 71, 0.94)',   // rich gold
      'rgba(204, 154, 6, 1.0)'      // deep ochre
    ],
    high_precision: [
      'rgba(252, 254, 255, 0)',
      'rgba(44, 132, 208, 0.22)',
      'rgba(86, 158, 196, 0.38)',
      'rgba(153, 189, 184, 0.54)',
      'rgba(201, 212, 176, 0.66)',
      'rgba(218, 209, 146, 0.76)',
      'rgba(227, 196, 112, 0.86)',
      'rgba(219, 186, 71, 0.94)',
      'rgba(204, 154, 6, 1.0)'
    ],
    threshold_focus: [
      'rgba(252, 254, 255, 0)',
      'rgba(93, 154, 196, 0.42)',
      'rgba(201, 212, 176, 0.8)',
      'rgba(233, 221, 146, 0.92)',
      'rgba(204, 154, 6, 1.0)'
    ]
  },
  silver: {
    name: 'Silver Potential',
    standard: [
      'rgba(252, 254, 255, 0)',
      'rgba(16, 35, 54, 0.3)',      // deep navy
      'rgba(93, 136, 187, 0.52)',   // steel blue
      'rgba(179, 203, 228, 0.68)',  // light blue
      'rgba(214, 214, 214, 0.86)',  // pale silver
      'rgba(161, 168, 178, 1.0)'    // medium gray
    ],
    high_precision: [
      'rgba(252, 254, 255, 0)',
      'rgba(31, 63, 98, 0.24)',
      'rgba(75, 120, 170, 0.42)',
      'rgba(126, 162, 203, 0.58)',
      'rgba(176, 196, 224, 0.72)',
      'rgba(204, 204, 204, 0.86)',
      'rgba(170, 178, 188, 0.94)',
      'rgba(130, 138, 148, 1.0)'
    ],
    threshold_focus: [
      'rgba(252, 254, 255, 0)',
      'rgba(75, 120, 170, 0.36)',
      'rgba(159, 184, 210, 0.76)',
      'rgba(198, 198, 198, 0.92)',
      'rgba(150, 155, 164, 1.0)'
    ]
  },
  copper: {
    name: 'Copper Potential',
    standard: [
      'rgba(252, 254, 255, 0)',
      'rgba(112, 203, 194, 0.3)',
      'rgba(146, 201, 150, 0.52)',
      'rgba(178, 189, 130, 0.68)',
      'rgba(200, 155, 118, 0.86)',
      'rgba(218, 132, 88, 1.0)'
    ],
    high_precision: [
      'rgba(252, 254, 255, 0)',
      'rgba(124, 208, 199, 0.2)',
      'rgba(150, 204, 154, 0.38)',
      'rgba(176, 190, 132, 0.52)',
      'rgba(202, 168, 112, 0.68)',
      'rgba(215, 150, 102, 0.8)',
      'rgba(223, 132, 85, 0.92)',
      'rgba(205, 94, 70, 1.0)'
    ],
    threshold_focus: [
      'rgba(252, 254, 255, 0)',
      'rgba(192, 208, 160, 0.38)',
      'rgba(215, 160, 112, 0.78)',
      'rgba(218, 130, 92, 0.94)',
      'rgba(218, 102, 68, 1.0)'
    ]
  }
};

export const HEATMAP_COLOR_SCHEMES = COLOR_SCHEMES;

const ContinuousHeatMapLayer: React.FC<ContinuousHeatMapLayerProps> = ({
  map,
  heatMapData,
  mineralType,
  visible,
  onVisibilityChange,
  onPointClick,
  showUncertainty = false,
  threshold = DEFAULT_ANALYSIS_GRID_SETTINGS.threshold,
  blur = DEFAULT_ANALYSIS_GRID_SETTINGS.blur,
  radius = DEFAULT_ANALYSIS_GRID_SETTINGS.radius,
  colorSchemeMode = DEFAULT_ANALYSIS_GRID_SETTINGS.colorScheme,
  scalingMode = DEFAULT_ANALYSIS_GRID_SETTINGS.scalingMode,
  focusRangeOverride,
  customFocusRange = DEFAULT_ANALYSIS_GRID_SETTINGS.customFocusRange,
  intensityGamma: intensityGammaProp = DEFAULT_ANALYSIS_GRID_SETTINGS.intensityGamma,
  customGamma = DEFAULT_ANALYSIS_GRID_SETTINGS.customGamma,
  controlsVariant = 'panel',
  controlsContainerId,
  onSettingsChange,
  analysisId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localThreshold, setLocalThreshold] = useState(threshold);
  const [localBlur, setLocalBlur] = useState(blur);
  const [localRadius, setLocalRadius] = useState(radius);
  const [showAdaptiveScaling, setShowAdaptiveScaling] = useState(showUncertainty);
  const [localColorSchemeMode, setLocalColorSchemeMode] = useState<ColorSchemeMode>(colorSchemeMode);
  const [localScalingMode, setLocalScalingMode] = useState<GridScalingMode>(scalingMode);

  const heatmapLayerRef = useRef<Heatmap | null>(null);

  // Cache for processed features to avoid re-processing same data
  const featuresCache = useRef<globalThis.Map<string, Feature[]>>(new globalThis.Map());

  useEffect(() => {
    setLocalThreshold(threshold);
  }, [threshold]);

  useEffect(() => {
    setShowAdaptiveScaling(showUncertainty);
  }, [showUncertainty]);

  useEffect(() => {
    setLocalBlur(blur);
  }, [blur]);

  useEffect(() => {
    setLocalRadius(radius);
  }, [radius]);

  useEffect(() => {
    setLocalColorSchemeMode(colorSchemeMode);
  }, [colorSchemeMode]);

  useEffect(() => {
    setLocalScalingMode(scalingMode);
  }, [scalingMode]);

  useEffect(() => {
    if (Array.isArray(focusRangeOverride) && focusRangeOverride.length >= 2) {
      const [rawMin, rawMax] = focusRangeOverride;
      const clampedMin = Math.max(0, Math.min(0.98, Math.min(rawMin, rawMax - 0.01)));
      const clampedMax = Math.min(1, Math.max(clampedMin + 0.01, rawMax));
      setFocusRange([Number(clampedMin.toFixed(3)), Number(clampedMax.toFixed(3))]);
      setHasCustomFocusRange(Boolean(customFocusRange));
    } else if (!customFocusRange) {
      setFocusRange(DEFAULT_ANALYSIS_GRID_SETTINGS.focusRange);
      setHasCustomFocusRange(false);
    }
  }, [focusRangeOverride, customFocusRange]);

  useEffect(() => {
    setIntensityGamma(intensityGammaProp);
    setHasCustomGamma(Boolean(customGamma));
  }, [intensityGammaProp, customGamma]);
  
  // Calculate statistics for adaptive scaling
  const statistics = useMemo(() => {
    if (!heatMapData || !Array.isArray(heatMapData.probabilities)) {
      return null;
    }

    const probs = heatMapData.probabilities.filter((p) => Number.isFinite(p)) as number[];
    if (!probs.length) {
      const [fallbackMin, fallbackMax] = DEFAULT_ANALYSIS_GRID_SETTINGS.focusRange;
      const mid = Number(((fallbackMin + fallbackMax) / 2).toFixed(3));
      return {
        min: fallbackMin,
        max: fallbackMax,
        mean: mid,
        median: mid,
        p10: fallbackMin,
        p25: fallbackMin,
        p75: fallbackMax,
        p90: fallbackMax,
        p95: fallbackMax,
        highPotentialCount: 0
      };
    }

    const sorted = [...probs].sort((a, b) => a - b);
    const safeIndex = (ratio: number) => {
      const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * ratio)));
      return sorted[idx] ?? sorted[sorted.length - 1] ?? sorted[0];
    };

    const sum = probs.reduce((a, b) => a + b, 0);

    return {
      min: sorted[0],
      max: sorted[sorted.length - 1],
      mean: sum / probs.length,
      median: safeIndex(0.5),
      p10: safeIndex(0.1),
      p25: safeIndex(0.25),
      p75: safeIndex(0.75),
      p90: safeIndex(0.9),
      p95: safeIndex(0.95),
      highPotentialCount: probs.filter(p => p > 0.7).length
    };
  }, [heatMapData]);

  const [focusRange, setFocusRange] = useState<[number, number]>(() => {
    if (Array.isArray(focusRangeOverride) && focusRangeOverride.length >= 2) {
      const [rawMin, rawMax] = focusRangeOverride;
      const clampedMin = Math.max(0, Math.min(0.98, Math.min(rawMin, rawMax - 0.01)));
      const clampedMax = Math.min(1, Math.max(clampedMin + 0.01, rawMax));
      return [Number(clampedMin.toFixed(3)), Number(clampedMax.toFixed(3))];
    }
    return DEFAULT_ANALYSIS_GRID_SETTINGS.focusRange;
  });
  const [hasCustomFocusRange, setHasCustomFocusRange] = useState(Boolean(customFocusRange));
  const [intensityGamma, setIntensityGamma] = useState(intensityGammaProp);
  const [hasCustomGamma, setHasCustomGamma] = useState(Boolean(customGamma));

  const settingsChangeRef = useRef(onSettingsChange);

  const emitSettingsUpdate = useCallback((updates: Partial<GridSettings>) => {
    if (!updates || !settingsChangeRef.current) {
      return;
    }
    settingsChangeRef.current(updates);
  }, []);

  useEffect(() => {
    settingsChangeRef.current = onSettingsChange;
  }, [onSettingsChange]);

  const dataSignature = useMemo(() => {
    if (!heatMapData || !heatMapData.probabilities?.length) {
      return null;
    }
    const probs = heatMapData.probabilities;
    const len = probs.length;
    const sampleIdx = [0, Math.floor(len / 2), Math.max(len - 1, 0)];
    const sample = sampleIdx
      .map((idx) => probs[Math.min(Math.max(idx, 0), len - 1)] ?? 0)
      .map((v) => v.toFixed(5))
      .join('|');
    const metaKey = (heatMapData.metadata as any)?.analysis_id
      ?? (heatMapData.metadata?.bounds ? heatMapData.metadata.bounds.join(',') : '');
    return `${len}:${sample}:${metaKey}`;
  }, [heatMapData]);

  const lastSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!dataSignature) {
      return;
    }
    if (lastSignatureRef.current !== dataSignature) {
      lastSignatureRef.current = dataSignature;
      setHasCustomFocusRange(false);
      setHasCustomGamma(false);
    }
  }, [dataSignature]);

  useEffect(() => {
    if (!statistics || hasCustomFocusRange) {
      return;
    }

    const suggestedMin = Math.max(0, Math.min(statistics.p75 - 0.05, statistics.median - 0.03, statistics.max - 0.12));
    const suggestedMax = Math.min(1, Math.max(statistics.p95 + 0.01, statistics.max));
    const clampedMin = Number(Math.max(0, Math.min(suggestedMin, statistics.min)).toFixed(3));
    const clampedMax = Number(Math.min(1, Math.max(suggestedMax, statistics.max)).toFixed(3));
    setFocusRange((prev) => {
      if (Math.abs(prev[0] - clampedMin) < 1e-4 && Math.abs(prev[1] - clampedMax) < 1e-4) {
        return prev;
      }
      const autoRange: [number, number] = [clampedMin, clampedMax];
      emitSettingsUpdate({ focusRange: autoRange, customFocusRange: false });
      return autoRange;
    });
  }, [statistics, hasCustomFocusRange, emitSettingsUpdate]);

  useEffect(() => {
    if (!statistics || hasCustomGamma) {
      return;
    }
    const spread = statistics.max - statistics.min;
    const recommendedGamma = spread < 0.05 ? 3.2 : spread < 0.1 ? 2.4 : 1.6;
    const rounded = Number(recommendedGamma.toFixed(1));
    setIntensityGamma((prev) => {
      if (Math.abs(prev - rounded) < 1e-3) {
        return prev;
      }
      emitSettingsUpdate({ intensityGamma: rounded, customGamma: false });
      return rounded;
    });
  }, [statistics, hasCustomGamma, emitSettingsUpdate]);

  const normalizeProbability = useCallback((value: number) => {
    const [minFocus, maxFocus] = focusRange;
    const range = maxFocus - minFocus;
    if (range <= 0) {
      return 0;
    }
    if (value <= minFocus) {
      return 0;
    }
    if (value >= maxFocus) {
      return 1;
    }
    return (value - minFocus) / range;
  }, [focusRange]);
  
  // Scaling functions for different modes
  const applyScaling = useCallback((value: number, mode: typeof localScalingMode): number => {
    switch (mode) {
      case 'linear':
        return value;
      case 'quadratic':
        return Math.pow(value, 2);
      case 'logarithmic':
        // Log scaling with offset to avoid log(0)
        return Math.log10(value * 9 + 1); // Maps [0,1] to [0,1]
      case 'sigmoid':
        // Sigmoid curve for smooth transitions around threshold
        const k = 10; // Steepness factor
        const midpoint = 0.7; // Focus around 70% threshold
        return 1 / (1 + Math.exp(-k * (value - midpoint)));
      default:
        return value;
    }
  }, []);

  // Create heat map features with caching
  const heatmapFeatures = useMemo(() => {
    if (!heatMapData) return [];

    // Generate cache key from analysis ID and processing parameters
    const cacheKey = analysisId
      ? `${analysisId}_${localThreshold}_${showAdaptiveScaling}_${localScalingMode}_${intensityGamma}`
      : null;

    // Check cache first if we have a cache key
    if (cacheKey && featuresCache.current.has(cacheKey)) {
      return featuresCache.current.get(cacheKey)!;
    }

    const features: Feature[] = [];

    // When probabilities collapse into only a few distinct levels (due to backend caps or calibration floors),
    // stretch those discrete buckets across 0-1 so the heat map keeps useful contrast.
    const bucketPrecision = 0.0001;
    const bucketSet = new Set<number>();
    heatMapData.probabilities.forEach((probability) => {
      if (!Number.isFinite(probability) || probability < localThreshold) {
        return;
      }
      const rounded = Math.round(probability / bucketPrecision) * bucketPrecision;
      bucketSet.add(rounded);
    });

    const sortedBuckets = Array.from(bucketSet).sort((a, b) => a - b);
    const bucketMap = new globalThis.Map<number, number>();
    let contrastBlend = 0;

    if (sortedBuckets.length >= 2 && sortedBuckets.length <= 16) {
      const denom = Math.max(1, sortedBuckets.length - 1);
      sortedBuckets.forEach((value, index) => {
        bucketMap.set(value, denom === 0 ? 0.5 : index / denom);
      });

      if (sortedBuckets.length <= 4) {
        contrastBlend = 0.85;
      } else if (sortedBuckets.length <= 8) {
        contrastBlend = 0.7;
      } else {
        contrastBlend = 0.55;
      }
    }

    heatMapData.coordinates.forEach((coord, index) => {
      const probability = heatMapData.probabilities[index];

      // Apply threshold filtering
      if (probability < localThreshold) return;

      const [lat, lon] = coord;
      const point = new Point(fromLonLat([lon, lat]));

      let normalized = normalizeProbability(probability);
      normalized = Math.max(0, Math.min(1, normalized));

      if (contrastBlend > 0) {
        const rounded = Math.round(probability / bucketPrecision) * bucketPrecision;
        const stretched = bucketMap.get(rounded);
        if (stretched !== undefined) {
          normalized = normalized * (1 - contrastBlend) + stretched * contrastBlend;
        }
      }

      const scaledBase = showAdaptiveScaling ?
        applyScaling(normalized, localScalingMode) :
        normalized;

      const gammaAdjusted = Math.pow(Math.max(0, Math.min(1, scaledBase)), intensityGamma);
      const finalWeight = Math.max(0.0001, Math.min(1, gammaAdjusted));

      const feature = new Feature({
        geometry: point,
        weight: finalWeight
      });

      // Store additional properties for click handling
      feature.set('probability', probability);
      feature.set('uncertainty', heatMapData.uncertainties?.[index]);
      feature.set('lat', lat);
      feature.set('lon', lon);
      feature.set('details', heatMapData.pointDetails?.[index] || null);
      feature.set('mineral_type', mineralType);

      features.push(feature);
    });

    // Store in cache if we have a cache key
    if (cacheKey) {
      featuresCache.current.set(cacheKey, features);

      // Limit cache size to prevent memory issues (keep last 10 analyses)
      if (featuresCache.current.size > 10) {
        const firstKey = featuresCache.current.keys().next().value;
        featuresCache.current.delete(firstKey);
      }
    }

    return features;
  }, [heatMapData, localThreshold, showAdaptiveScaling, localScalingMode, applyScaling, normalizeProbability, intensityGamma, mineralType, analysisId]);

  const lastThresholdRef = useRef<number | null>(null);
  useEffect(() => {
    const rounded = Number(localThreshold.toFixed(5));
    if (lastThresholdRef.current === rounded) {
      return;
    }
    lastThresholdRef.current = rounded;
    emitSettingsUpdate({ threshold: localThreshold });
  }, [localThreshold, emitSettingsUpdate]);

  const lastModulatedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (lastModulatedRef.current === showAdaptiveScaling) {
      return;
    }
    lastModulatedRef.current = showAdaptiveScaling;
    emitSettingsUpdate({ modulated: showAdaptiveScaling });
  }, [showAdaptiveScaling, emitSettingsUpdate]);

  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!controlsContainerId) {
      setPortalElement(null);
      return;
    }
    const updatePortalTarget = () => {
      const el = document.getElementById(controlsContainerId);
      if (el !== portalElement) {
        setPortalElement(el);
      }
    };

    updatePortalTarget();

    const observer = new MutationObserver(() => updatePortalTarget());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [controlsContainerId, portalElement]);

  // Track zoom level for re-rendering on zoom change
  const [currentZoomLevel, setCurrentZoomLevel] = useState(10);
  
  useEffect(() => {
    if (!map) return;
    
    const updateZoom = () => {
      const zoom = map.getView().getZoom() || 10;
      setCurrentZoomLevel(zoom);
    };
    
    // Listen for zoom changes
    map.getView().on('change:resolution', updateZoom);
    updateZoom(); // Set initial zoom
    
    return () => {
      map.getView().un('change:resolution', updateZoom);
    };
  }, [map]);
  
  // Create and update heat map layer
  useEffect(() => {
    if (!map || !visible || heatmapFeatures.length === 0) {
      if (heatmapLayerRef.current) {
        map?.removeLayer(heatmapLayerRef.current);
        heatmapLayerRef.current = null;
      }
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.info('[HeatMapLayer] features', heatmapFeatures.length, 'visible', visible);
    }

    setIsLoading(true);
    
    try {
      // Create vector source with features
      const vectorSource = new VectorSource({
        features: heatmapFeatures
      });
      
      // Get color gradient for mineral type and mode
      const mineralScheme = COLOR_SCHEMES[mineralType as keyof typeof COLOR_SCHEMES] || COLOR_SCHEMES.copper;
      const gradient = mineralScheme[localColorSchemeMode] || mineralScheme.high_precision;
      
      // Adjust blur and radius based on zoom level for consistent appearance
      // At low zoom (zoomed out): smaller blur/radius to prevent over-coverage
      // At high zoom (zoomed in): larger blur/radius to maintain visibility
      const zoomFactor = currentZoomLevel / 15; // Normalize zoom (usually 5-20)
      const adaptiveBlur = localBlur * Math.max(0.3, Math.min(1.5, zoomFactor));
      const adaptiveRadius = localRadius * Math.max(0.3, Math.min(2.0, zoomFactor * 1.2));
      
      // Create heat map layer
      const heatmapLayer = new Heatmap({
        source: vectorSource,
        blur: adaptiveBlur,
        radius: adaptiveRadius,
        gradient: gradient,
        weight: (feature) => {
          const weight = feature.get('weight') || 0;
          // Apply uncertainty modulation if enabled
          if (showUncertainty) {
            const uncertainty = feature.get('uncertainty') || 1;
            return weight * (2 - uncertainty); // Higher uncertainty = lower weight
          }
          return weight;
        }
      });
      
      // Remove old layer
      if (heatmapLayerRef.current) {
        map.removeLayer(heatmapLayerRef.current);
      }
      
      // Add new layer
      map.addLayer(heatmapLayer);
      heatmapLayerRef.current = heatmapLayer;

      // Set layer properties
      heatmapLayer.setVisible(true);
      heatmapLayer.setZIndex(999); // Below markers but above base map
      
      // Add click handler if provided
      if (onPointClick) {
        const clickHandler = (evt: any) => {
          const pixel = evt.pixel;
          const features = map.getFeaturesAtPixel(pixel);
          
          if (features && features.length > 0) {
            const feature = features[0];
            onPointClick({
              lat: feature.get('lat'),
              lon: feature.get('lon'),
              probability: feature.get('probability'),
              uncertainty: feature.get('uncertainty'),
              details: feature.get('details') || null
            });
          }
        };
        
        map.on('singleclick', clickHandler);
        
        // Store handler for cleanup
        (heatmapLayer as any)._clickHandler = clickHandler;
      }
      
    } catch (err) {
      console.error('Failed to create heat map layer:', err);
      setError(`Rendering error: ${err}`);
    } finally {
      setIsLoading(false);
    }
    
  }, [map, visible, heatmapFeatures, localBlur, localRadius, showUncertainty, mineralType, onPointClick, currentZoomLevel, localColorSchemeMode]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (map && heatmapLayerRef.current) {
        // Remove click handler if exists
        const layer = heatmapLayerRef.current as any;
        if (layer._clickHandler) {
          map.un('singleclick', layer._clickHandler);
        }
        map.removeLayer(heatmapLayerRef.current);
      }
    };
  }, [map]);
  
  const focusSliderBounds = useMemo(() => {
    const defaultRange = DEFAULT_ANALYSIS_GRID_SETTINGS.focusRange;
    const baseMin = (statistics?.p10 ?? defaultRange[0]) - 0.1;
    const baseMax = (statistics?.max ?? defaultRange[1]) + 0.05;
    let minBound = Number.isFinite(baseMin) ? (baseMin as number) : defaultRange[0];
    let maxBound = Number.isFinite(baseMax) ? (baseMax as number) : defaultRange[1];
    minBound = Math.max(0, Math.min(0.98, minBound));
    maxBound = Math.min(1, Math.max(minBound + 0.02, maxBound));
    if (maxBound - minBound < 0.02) {
      maxBound = Math.min(1, minBound + 0.02);
      minBound = Math.max(0, maxBound - 0.02);
    }
    return {
      min: Number(minBound.toFixed(3)),
      max: Number(maxBound.toFixed(3))
    };
  }, [statistics]);

  useEffect(() => {
    setFocusRange((prev) => {
      const clampedMin = Number(Math.max(focusSliderBounds.min, Math.min(prev[0], focusSliderBounds.max - 0.01)).toFixed(3));
      const clampedMax = Number(Math.min(focusSliderBounds.max, Math.max(prev[1], focusSliderBounds.min + 0.01)).toFixed(3));
      if (clampedMin === prev[0] && clampedMax === prev[1]) {
        return prev;
      }
      return [clampedMin, clampedMax];
    });
  }, [focusSliderBounds.min, focusSliderBounds.max]);

  if (!heatMapData) {
    if (controlsContainerId && portalElement) {
      return createPortal(<></>, portalElement);
    }
    return null;
  }

  const isLegend = controlsVariant === 'legend';

  const paperStyles = isLegend
    ? {
        p: 1.25,
        mb: 0,
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        width: '100%',
        maxWidth: '260px'
      }
    : {
        p: 2,
        mb: 2,
        backgroundColor: 'rgba(22, 27, 34, 0.95)',
        border: '1px solid #30363D',
        borderRadius: '8px',
        backdropFilter: 'blur(10px)'
      };

  const controlsContent = (
    <Paper sx={paperStyles}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GradientIcon sx={{ color: '#00D2FF', fontSize: isLegend ? 18 : 20 }} />
          <Typography sx={{
            color: '#00D2FF',
            fontFamily: 'monospace',
            fontSize: isLegend ? '12px' : '14px',
            fontWeight: 'bold'
          }}>
            CONTINUOUS HEAT MAP - {mineralType.toUpperCase()}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLoading && <CircularProgress size={16} />}
          
          {error && (
            <Tooltip title={error}>
              <WarningIcon sx={{ color: 'orange', fontSize: 18 }} />
            </Tooltip>
          )}
          
          <Tooltip title={visible ? "Hide Heat Map" : "Show Heat Map"}>
            <IconButton
              onClick={() => onVisibilityChange(!visible)}
              size="small"
              sx={{
                color: visible ? '#00D2FF' : '#6E7681',
                '&:hover': {
                  color: visible ? '#00B8E6' : '#C9D1D9'
                }
              }}
            >
              {visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Enhanced Metadata Display (legend only) */}
      {isLegend && heatMapData.metadata && (
        <Box sx={{
          mb: 2,
          p: isLegend ? 1.25 : 1.5,
          backgroundColor: isLegend ? 'rgba(13,17,23,0.55)' : 'rgba(13, 17, 23, 0.6)',
          borderRadius: '6px',
          border: '1px solid rgba(48, 54, 61, 0.5)'
        }}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip
              icon={<InfoIcon />}
              label={`High Potential: ${heatMapData.metadata.high_potential_count}`}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255, 179, 0, 0.1)',
                borderColor: '#FFB300',
                color: '#FFB300'
              }}
            />
            <Chip
              label={`Discovery Probability: ${(heatMapData.metadata.discovery_probability * 100).toFixed(1)}%`}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                borderColor: '#4CAF50',
                color: '#4CAF50'
              }}
            />
            {heatMapData.metadata.exploration_value && (
              <Chip
                label={`Exploration Value: ${heatMapData.metadata.exploration_value.toFixed(2)}`}
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(0, 210, 255, 0.1)',
                  borderColor: '#00D2FF',
                  color: '#00D2FF'
                }}
              />
            )}
          </Stack>
        </Box>
      )}
      
      {/* Statistics */}
      {statistics && (
        <Box sx={{
          mb: 2,
          p: isLegend ? 1.25 : 1.5,
          backgroundColor: isLegend ? 'rgba(13,17,23,0.55)' : 'rgba(13, 17, 23, 0.6)',
          borderRadius: '6px',
          border: '1px solid rgba(48, 54, 61, 0.5)'
        }}>
          <Typography sx={{ color: '#FFB300', fontSize: '10px', fontFamily: 'monospace', mb: 1 }}>
            PROBABILITY DISTRIBUTION
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '11px', fontFamily: 'monospace' }}>
            <span style={{ color: '#C9D1D9' }}>
              Range: {(statistics.min * 100).toFixed(1)}% - {(statistics.max * 100).toFixed(1)}%
            </span>
            <span style={{ color: '#C9D1D9' }}>
              Mean: {(statistics.mean * 100).toFixed(1)}%
            </span>
            <span style={{ color: '#C9D1D9' }}>
              Median: {(statistics.median * 100).toFixed(1)}%
            </span>
            <span style={{ color: '#C9D1D9' }}>
              P75: {(statistics.p75 * 100).toFixed(1)}%
            </span>
            <span style={{ color: showAdaptiveScaling ? '#4CAF50' : '#FFC107' }}>
              {showAdaptiveScaling ? '✓ Adaptive Scaling' : 'Linear Scaling'}
            </span>
            <span style={{ color: showUncertainty ? '#00D2FF' : '#6E7681' }}>
              {showUncertainty ? '✓ Uncertainty Weighting' : 'Probability Only'}
            </span>
          </Box>
        </Box>
      )}
      
      {/* Controls */}
      {visible && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isLegend ? 1.5 : 2 }}>
          {/* Threshold slider */}
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: isLegend ? '10px' : '11px',
              mb: isLegend ? 0.75 : 1
            }}>
              MINIMUM THRESHOLD: {(localThreshold * 100).toFixed(0)}%
            </Typography>
            <Slider
              value={localThreshold}
              onChange={(_, value) => setLocalThreshold(value as number)}
              min={0.0}
              max={0.9}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
              sx={{
                color: '#00D2FF',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#00D2FF',
                  '&:hover': {
                    boxShadow: '0 0 0 8px rgba(0, 210, 255, 0.16)'
                  }
                },
                '& .MuiSlider-track': {
                  background: 'linear-gradient(90deg, #00D2FF 0%, #00B8E6 100%)'
                }
              }}
            />
          </Box>
          
          {/* Blur slider */}
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: isLegend ? '10px' : '11px',
              mb: isLegend ? 0.75 : 1
            }}>
              BLUR RADIUS: {localBlur}px
            </Typography>
            <Slider
              value={localBlur}
              onChange={(_, value) => {
                const numeric = Array.isArray(value) ? value[0] : (value as number);
                setLocalBlur(numeric);
                emitSettingsUpdate({ blur: numeric });
              }}
              min={2}
              max={20}
              step={1}
              valueLabelDisplay="auto"
              sx={{
                color: '#FFB300',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#FFB300'
                }
              }}
            />
          </Box>
          
          {/* Point radius slider */}
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: isLegend ? '10px' : '11px',
              mb: isLegend ? 0.75 : 1
            }}>
              POINT RADIUS: {localRadius}px
            </Typography>
            <Slider
              value={localRadius}
              onChange={(_, value) => {
                const numeric = Array.isArray(value) ? value[0] : (value as number);
                setLocalRadius(numeric);
                emitSettingsUpdate({ radius: numeric });
              }}
              min={5}
              max={50}
              step={5}
              valueLabelDisplay="auto"
              sx={{
                color: '#4CAF50',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#4CAF50'
                }
              }}
            />
            </Box>

            {/* Focus range slider */}
            <Box>
              <Typography sx={{
                color: '#00D2FF',
                fontFamily: 'monospace',
                fontSize: isLegend ? '10px' : '11px',
                mb: isLegend ? 0.75 : 1
              }}>
                FOCUS WINDOW: {(focusRange[0] * 100).toFixed(1)}% – {(focusRange[1] * 100).toFixed(1)}%
              </Typography>
              <Slider
                value={focusRange}
                min={focusSliderBounds.min}
                max={focusSliderBounds.max}
                step={0.005}
                onChange={(_, value) => {
                  if (Array.isArray(value) && value.length === 2) {
                    const [min, max] = value as number[];
                    setHasCustomFocusRange(true);
                  const clampedMin = Number(Math.max(0, Math.min(min, max - 0.01)).toFixed(3));
                  const clampedMax = Number(Math.min(1, Math.max(max, min + 0.01)).toFixed(3));
                  const updatedRange: [number, number] = [clampedMin, clampedMax];
                  setFocusRange(updatedRange);
                  emitSettingsUpdate({ focusRange: updatedRange, customFocusRange: true });
                }
              }}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${(v * 100).toFixed(1)}%`}
              sx={{
                color: '#FFB300',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#FFB300'
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
              <Chip
                label="AUTO (P75–MAX)"
                size="small"
                onClick={() => {
                  if (!statistics) return;
                  const suggestedMin = Math.max(0, Math.min(statistics.p75 - 0.05, statistics.median - 0.03, statistics.max - 0.12));
                  const suggestedMax = Math.min(1, Math.max(statistics.p95 + 0.01, statistics.max));
                  const autoRange: [number, number] = [
                    Number(Math.max(0, Math.min(suggestedMin, suggestedMax - 0.02)).toFixed(3)),
                    Number(Math.min(1, Math.max(suggestedMax, suggestedMin + 0.02)).toFixed(3))
                  ];
                  setFocusRange(autoRange);
                  setHasCustomFocusRange(false);
                  emitSettingsUpdate({ focusRange: autoRange, customFocusRange: false });
                }}
                sx={{
                  backgroundColor: !hasCustomFocusRange ? 'rgba(255, 179, 0, 0.2)' : 'transparent',
                  borderColor: '#FFB300',
                  color: '#FFB300',
                  border: '1px solid'
                }}
              />
              <Chip
                label="FULL RANGE"
                size="small"
                onClick={() => {
                  setFocusRange([0, 1]);
                  setHasCustomFocusRange(true);
                  emitSettingsUpdate({ focusRange: [0, 1], customFocusRange: true });
                }}
                sx={{
                  borderColor: '#30363D',
                  color: '#6E7681',
                  border: '1px solid'
                }}
              />
            </Box>
          </Box>

          {/* Precision (gamma) slider */}
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: isLegend ? '10px' : '11px',
              mb: isLegend ? 0.75 : 1
            }}>
              COLOR PRECISION (γ): {intensityGamma.toFixed(1)}x
            </Typography>
            <Slider
              value={intensityGamma}
              min={0.6}
              max={5}
              step={0.1}
              onChange={(_, value) => {
                setHasCustomGamma(true);
                const numeric = Array.isArray(value) ? value[0] : (value as number);
                const rounded = Number(numeric.toFixed(2));
                setIntensityGamma(rounded);
                emitSettingsUpdate({ intensityGamma: rounded, customGamma: true });
              }}
              valueLabelDisplay="auto"
              sx={{
                color: '#E91E63',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#E91E63'
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
              {[1.0, 2.0, 3.0, 4.0].map((preset) => (
                <Chip
                  key={preset}
                  label={`${preset.toFixed(1)}x`}
                  size="small"
                  onClick={() => {
                    setIntensityGamma(preset);
                    setHasCustomGamma(true);
                    emitSettingsUpdate({ intensityGamma: preset, customGamma: true });
                  }}
                  sx={{
                    backgroundColor: Math.abs(intensityGamma - preset) < 0.05 ? 'rgba(233, 30, 99, 0.2)' : 'transparent',
                    borderColor: '#E91E63',
                    color: '#E91E63',
                    border: '1px solid'
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Color Scheme Selector */}
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: isLegend ? '10px' : '11px',
              mb: isLegend ? 0.75 : 1
            }}>
              COLOR SCHEME MODE
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(['standard', 'high_precision', 'threshold_focus'] as ColorSchemeMode[]).map(mode => (
                <Chip
                  key={mode}
                  label={mode.replace('_', ' ').toUpperCase()}
                  size="small"
                  onClick={() => {
                    setLocalColorSchemeMode(mode);
                    emitSettingsUpdate({ colorScheme: mode });
                  }}
                  sx={{
                    backgroundColor: localColorSchemeMode === mode ? 'rgba(0, 210, 255, 0.2)' : 'transparent',
                    borderColor: localColorSchemeMode === mode ? '#00D2FF' : '#30363D',
                    color: localColorSchemeMode === mode ? '#00D2FF' : '#6E7681',
                    border: '1px solid',
                    fontFamily: 'monospace',
                    fontSize: '10px',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#00D2FF',
                      backgroundColor: 'rgba(0, 210, 255, 0.1)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
          
          {/* Scaling Mode Selector */}
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: isLegend ? '10px' : '11px',
              mb: isLegend ? 0.75 : 1
            }}>
              SCALING MODE
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(['linear', 'quadratic', 'logarithmic', 'sigmoid'] as const).map(mode => (
                <Chip
                  key={mode}
                  label={mode.toUpperCase()}
                  size="small"
                  onClick={() => {
                    setLocalScalingMode(mode);
                    emitSettingsUpdate({ scalingMode: mode });
                  }}
                  sx={{
                    backgroundColor: localScalingMode === mode ? 'rgba(255, 179, 0, 0.2)' : 'transparent',
                    borderColor: localScalingMode === mode ? '#FFB300' : '#30363D',
                    color: localScalingMode === mode ? '#FFB300' : '#6E7681',
                    border: '1px solid',
                    fontFamily: 'monospace',
                    fontSize: '10px',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#FFB300',
                      backgroundColor: 'rgba(255, 179, 0, 0.1)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
          
          {/* Toggle switches */}
          <FormControlLabel
            control={
              <Switch
                checked={showAdaptiveScaling}
                onChange={(e) => setShowAdaptiveScaling(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4CAF50'
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4CAF50'
                  }
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: isLegend ? '10px' : '12px', fontFamily: 'monospace', color: showAdaptiveScaling ? '#4CAF50' : '#6E7681' }}>
                Enable Scaling (use {localScalingMode} mode)
              </Typography>
            }
          />
        </Box>
      )}
    </Paper>
  );

  if (!controlsContainerId) {
    return null;
  }

  return portalElement ? createPortal(controlsContent, portalElement) : null;
};

export default React.memo(ContinuousHeatMapLayer);
